import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles, Button } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { getI18n } from "../../../utils/intl";
import styles from './styles';

const PanelTitleDescription = (props) => {
  const { title, description, handleTitleDescChange } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    editFlow: false,
    newTitle: title,
    newDesc: description,
    newTitleLength: title.length,
    newDescLength: description.length,
    error: false,
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const titlePropRef = useRef(title);
  useEffect(() => {
    if (titlePropRef.current !== title) {
      setState({ title });
    }
    titlePropRef.current = title;
  }, [title]);

  const descriptionPropRef = useRef(description);
  useEffect(() => {
    if (descriptionPropRef.current !== description) {
      setState({ description });
    }
    descriptionPropRef.current = description;
  }, [description]);

  const onCancel = () => {
    editFlowToggle();
    setState({
      newTitle: title,
      newTitleLength: title.length,
      newDesc: description,
      newDescLength: description.length,
      error: false,
    });
  }

  const onApply = () => {
    const newTitleDesc = {
      title: state.newTitle.trim(),
      description: state.newDesc.trim(),
    };
    handleTitleDescChange(newTitleDesc);
    editFlowToggle();
  }

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setState({
      newTitle: title,
      newTitleLength: title.length,
      error: title.trim().length === 0,
    });
  }

  const handleDescChange = (e) => {
    const description = e.target.value;
    setState({
      newDesc: description,
      newDescLength: description.length,
    });
  }

  const editFlowToggle = () => {
    setState(currentValue => ({
      editFlow: !currentValue.editFlow,
      newTitle: title,
      newTitleLength: title.length,
      newDesc: description,
      newDescLength: description.length,
      error: false,
    }));
  }

  const renderTitleDesc = () => {
    return (
      <div>
        <span className={classes.title}>{title}</span>
          <EditIcon
            className={classes.iconColor}
            onClick={editFlowToggle}
          />
        <div className={classes.description}>
          <span>{description}</span>
        </div>
      </div>
    );
  }

  const renderEditTileDesc = () => {
    const { newTitle, newDesc, newTitleLength, newDescLength, error } = state;
    return (
      <div>
        <span className={classes.editText}>
          {intl.getI18nMessage('label.analyticsDashboard.customReport.chartPanel.editText.label')}
        </span>
        <div className={classes.inputSection}>
          <span className={classes.inputLabel}>
            {intl.getI18nMessage('label.analyticsDashboard.customReport.chartPanel.Title.label')}
          </span>
          <input
            type="text"
            className={classes.inputBox}
            placeholder="Panel Title"
            value={newTitle}
            onChange={handleTitleChange}
            maxLength="80"
            required
          />
          { error && <span className={classes.errorMessage}>
            {intl.getI18nMessage('label.analyticsDashboard.customReport.chartPanel.Title.errorMessage')}
          </span>}
          <span className={classes.charCount}>{newTitleLength}/80 characters</span>
        </div>
        <div className={classes.inputSection}>
          <span className={classes.inputLabel}>Description</span>
          <span className={classes.optionalLabel}>optional</span>
          <textarea
            type="text"
            className={clsx(classes.inputBox, classes.descInputBox)}
            placeholder="Panel Description" value={newDesc}
            onChange={handleDescChange}
            maxLength="400"
          />
          <span className={classes.charCount}>{newDescLength}/400 characters</span>
        </div>
        <div className={classes.buttonBar}>
          <Button
            color="secondary"
            disabled={error}
            onClick={onApply}
            variant='contained'
          >
            {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.button.apply')}
          </Button>
          <Button
            className={classes.cancelBtn}
            color="primary"
            onClick={onCancel}
            variant='outlined'
          >
            {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.button.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  const { editFlow } = state;
  return (
    <div>
      { editFlow ? renderEditTileDesc() : renderTitleDesc() }
    </div>
  );
}

PanelTitleDescription.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  handleTitleDescChange: PropTypes.func,
};

export default PanelTitleDescription;
